<script setup>
import dayjs from 'dayjs'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { DAYS_PER_WEEK, daysBetween, dayKey, useCalendarFormatters } from '@/composables/event-calendar'

const MINI_MONTH_WEEKS = 6

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  rangeStart: {
    type: Object,
    default: null,
  },
  rangeEnd: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['update:modelValue'])

const { locale } = useI18n()
const formatters = computed(() => useCalendarFormatters(locale.value))

const browseOffset = ref(0)
watch(() => dayKey(props.modelValue.startOf('month')), () => {
  browseOffset.value = 0
})

const month = computed(() => props.modelValue.startOf('month').add(browseOffset.value, 'month'))
const gridStart = computed(() => month.value.startOf('isoWeek'))
const days = computed(() => daysBetween(gridStart.value, gridStart.value.add(MINI_MONTH_WEEKS * DAYS_PER_WEEK, 'day')))
const weekdays = computed(() => days.value.slice(0, DAYS_PER_WEEK).map(day => formatters.value.weekdayNarrow.format(day.toDate())))
const todayKey = dayKey(dayjs())

const inRange = (day) => props.rangeStart && props.rangeEnd && !day.isBefore(props.rangeStart) && day.isBefore(props.rangeEnd)

const dayClasses = (day) => {
  const highlighted = inRange(day)
  return {
    'mini-month__day--outside': !day.isSame(month.value, 'month'),
    'mini-month__day--today': dayKey(day) === todayKey,
    'mini-month__day--selected': dayKey(day) === dayKey(props.modelValue),
    'mini-month__day--range': highlighted,
    'mini-month__day--range-start': highlighted && (!inRange(day.subtract(1, 'day')) || day.isoWeekday() === 1),
    'mini-month__day--range-end': highlighted && (!inRange(day.add(1, 'day')) || day.isoWeekday() === DAYS_PER_WEEK),
  }
}

const onPick = (day) => {
  browseOffset.value = 0
  emit('update:modelValue', day)
}
</script>
<template>
  <div class="mini-month">
    <div class="mini-month__header">
      <span class="mini-month__title">{{ formatters.monthYear.format(month.toDate()) }}</span>
      <q-btn
        flat
        round
        dense
        size="sm"
        icon="mdi-chevron-left"
        @click="browseOffset--"
      />
      <q-btn
        flat
        round
        dense
        size="sm"
        icon="mdi-chevron-right"
        @click="browseOffset++"
      />
    </div>
    <div class="mini-month__grid">
      <span
        v-for="(weekday, index) in weekdays"
        :key="`weekday-${index}`"
        class="mini-month__weekday"
      >{{ weekday }}</span>
      <button
        v-for="day in days"
        :key="dayKey(day)"
        type="button"
        class="mini-month__day"
        :class="dayClasses(day)"
        @click="onPick(day)"
      >
        <span class="mini-month__number">{{ day.date() }}</span>
      </button>
    </div>
  </div>
</template>
<style scoped lang="scss">
.mini-month {
  user-select: none;
}

.mini-month__header {
  display: flex;
  align-items: center;
  gap: 0.125rem;
  padding: 0 0.25rem 0.375rem 0.5rem;
}

.mini-month__title {
  flex: 1;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: capitalize;
}

.mini-month__grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 0.125rem;
}

.mini-month__weekday {
  padding: 0.25rem 0;
  font-size: 0.6875rem;
  font-weight: 600;
  text-align: center;
  text-transform: uppercase;
  color: var(--cal-text-muted);
}

.mini-month__day {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 1.875rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 0.75rem;
  cursor: pointer;
}

.mini-month__number {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.mini-month__day:hover .mini-month__number {
  background: color-mix(in srgb, var(--q-primary) 12%, transparent);
}

.mini-month__day:active .mini-month__number {
  transform: scale(0.9);
}

.mini-month__day--outside {
  color: var(--cal-text-muted);
  opacity: 0.6;
}

.mini-month__day--range::before {
  content: '';
  position: absolute;
  inset: 0.0625rem 0;
  background: color-mix(in srgb, var(--q-primary) 10%, transparent);
}

.mini-month__day--range-start::before {
  left: 0.125rem;
  border-top-left-radius: 1rem;
  border-bottom-left-radius: 1rem;
}

.mini-month__day--range-end::before {
  right: 0.125rem;
  border-top-right-radius: 1rem;
  border-bottom-right-radius: 1rem;
}

.mini-month__day--selected .mini-month__number {
  background: color-mix(in srgb, var(--q-primary) 22%, transparent);
  color: var(--q-primary);
  font-weight: 700;
}

.mini-month__day--today .mini-month__number,
.mini-month__day--today:hover .mini-month__number {
  background: var(--q-primary);
  color: white;
  font-weight: 700;
  box-shadow: 0 0.25rem 0.625rem -0.25rem var(--q-primary);
}
</style>
